import { useCallback, useEffect, useState } from 'react'
import { AuthClientError, type AuthClient, type AuthUser } from '../auth/client'
import { AUTH_FAILURE_COPY } from '../auth/copy'

export type AuthState = {
  user: AuthUser | null
  initialising: boolean
  restoreError: string | null
  signOutError: string | null
  signOut: () => Promise<void>
  retryRestore: () => void
}

const signOutMessage = (error: unknown): string =>
  error instanceof AuthClientError ? error.message : AUTH_FAILURE_COPY.signOutUnknown

export const useAuthState = (auth: AuthClient, onSignedOut: () => void): AuthState => {
  const [user, setUser] = useState<AuthUser | null>(auth.current())
  const [initialising, setInitialising] = useState(true)
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const [signOutError, setSignOutError] = useState<string | null>(null)

  useEffect(
    () =>
      auth.subscribe(
        (next) => {
          setUser(next)
          setInitialising(false)
          setRestoreError(null)
          setSignOutError(null)
          if (next === null) onSignedOut()
        },
        (error) => {
          setRestoreError(error.message)
          setInitialising(false)
        },
      ),
    [auth, onSignedOut],
  )

  const signOut = useCallback(async () => {
    setSignOutError(null)
    await auth.signOut().catch((error: unknown) => setSignOutError(signOutMessage(error)))
  }, [auth])

  const retryRestore = useCallback(() => {
    setRestoreError(null)
    setInitialising(true)
    auth.retryRestore()
  }, [auth])

  return { user, initialising, restoreError, signOutError, signOut, retryRestore }
}
